import { supabase } from './supabase';
import type { GoogleTokens, GoogleCalendar, GooglePhotoAlbum, CalendarEvent } from '../types';

// Client ID is safe to expose in frontend - it's public
// Client Secret is kept server-side in Supabase Edge Functions
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const REDIRECT_URI = import.meta.env.VITE_GOOGLE_REDIRECT_URI || `${window.location.origin}/auth/google/callback`;

const SCOPES = [
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/photoslibrary.readonly',
].join(' ');

// ============================================
// OAUTH FLOW
// ============================================

export function getGoogleAuthUrl(householdId: string): string {
  if (!GOOGLE_CLIENT_ID) {
    console.error('VITE_GOOGLE_CLIENT_ID is not configured');
    return '';
  }

  console.log('OAuth redirect_uri:', REDIRECT_URI);
  console.log('OAuth scopes:', SCOPES);

  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: SCOPES,
    access_type: 'offline',
    prompt: 'consent',
    state: householdId,
  });

  const url = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  console.log('Full OAuth URL:', url);
  return url;
}

export async function exchangeCodeForTokens(
  code: string,
  householdId: string
): Promise<GoogleTokens | null> {
  try {
    console.log('Token exchange - redirect_uri being sent:', REDIRECT_URI);

    // Use Edge Function to exchange code for tokens securely
    // This keeps GOOGLE_CLIENT_SECRET server-side
    const { data, error } = await supabase.functions.invoke('google-auth-callback', {
      body: {
        code,
        householdId,
        redirectUri: REDIRECT_URI,
      },
    });

    if (error) {
      console.error('Token exchange failed:', error);
      return null;
    }

    if (!data?.success) {
      console.error('Token exchange failed:', data?.error);
      return null;
    }

    return data.tokens;
  } catch (err) {
    console.error('Token exchange error:', err);
    return null;
  }
}

async function getValidAccessToken(householdId: string): Promise<string | null> {
  try {
    // Use Edge Function to get valid access token (handles refresh server-side)
    const { data, error } = await supabase.functions.invoke('google-refresh-token', {
      body: { householdId },
    });

    if (error) {
      console.error('Failed to get valid access token:', error);
      return null;
    }

    if (!data?.success) {
      console.error('Failed to get valid access token:', data?.error);
      return null;
    }

    // Log the token info for debugging
    console.log('Got access token, checking what scopes it actually has...');

    // Verify token scopes by calling tokeninfo endpoint
    try {
      const tokenInfoResponse = await fetch(
        `https://oauth2.googleapis.com/tokeninfo?access_token=${data.accessToken}`
      );
      if (tokenInfoResponse.ok) {
        const tokenInfo = await tokenInfoResponse.json();
        console.log('=== TOKEN INFO ===');
        console.log('Scope:', tokenInfo.scope);
        console.log('Expires in:', tokenInfo.expires_in, 'seconds');
        console.log('Email:', tokenInfo.email);
        console.log('==================');
      }
    } catch (e) {
      console.log('Could not fetch token info:', e);
    }

    return data.accessToken;
  } catch (err) {
    console.error('Access token error:', err);
    return null;
  }
}

// ============================================
// CALENDAR API
// ============================================

export async function fetchGoogleCalendars(householdId: string): Promise<GoogleCalendar[]> {
  const accessToken = await getValidAccessToken(householdId);
  if (!accessToken) return [];

  try {
    const response = await fetch(
      'https://www.googleapis.com/calendar/v3/users/me/calendarList',
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!response.ok) return [];

    const data = await response.json();
    return data.items || [];
  } catch {
    return [];
  }
}

export async function fetchGoogleCalendarEvents(
  householdId: string,
  calendarId: string,
  timeMin: Date,
  timeMax: Date
): Promise<CalendarEvent[]> {
  const accessToken = await getValidAccessToken(householdId);
  if (!accessToken) return [];

  try {
    const params = new URLSearchParams({
      timeMin: timeMin.toISOString(),
      timeMax: timeMax.toISOString(),
      singleEvents: 'true',
      orderBy: 'startTime',
      maxResults: '100',
    });

    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?${params}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!response.ok) return [];

    const data = await response.json();
    
    return (data.items || []).map((event: any) => ({
      id: event.id,
      household_id: householdId,
      title: event.summary || 'Untitled',
      description: event.description,
      location: event.location,
      start_time: event.start.dateTime || event.start.date,
      end_time: event.end?.dateTime || event.end?.date,
      all_day: !event.start.dateTime,
      google_event_id: event.id,
      google_calendar_id: calendarId,
      source: 'google' as const,
      is_recurring: !!event.recurringEventId,
    }));
  } catch {
    return [];
  }
}

export async function syncGoogleCalendar(
  householdId: string,
  calendarId: string,
  familyMemberId?: string
): Promise<number> {
  const now = new Date();
  const timeMin = new Date(now);
  timeMin.setDate(timeMin.getDate() - 7); // Past week
  const timeMax = new Date(now);
  timeMax.setMonth(timeMax.getMonth() + 3); // Next 3 months

  const events = await fetchGoogleCalendarEvents(householdId, calendarId, timeMin, timeMax);
  
  if (events.length === 0) return 0;

  // Upsert events
  const { error } = await supabase.from('calendar_events').upsert(
    events.map(e => ({
      ...e,
      family_member_id: familyMemberId,
    })),
    { onConflict: 'google_event_id' }
  );

  if (error) {
    console.error('Failed to sync events:', error);
    return 0;
  }

  return events.length;
}

// ============================================
// PHOTOS API
// ============================================

export async function fetchGooglePhotoAlbums(householdId: string): Promise<GooglePhotoAlbum[]> {
  const accessToken = await getValidAccessToken(householdId);
  if (!accessToken) {
    console.error('No access token available for Photos API');
    return [];
  }

  console.log('Calling Photos API with token (first 30 chars):', accessToken.substring(0, 30));

  try {
    const response = await fetch(
      'https://photoslibrary.googleapis.com/v1/albums?pageSize=50',
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('=== PHOTOS API ERROR DETAILS ===');
      console.error('Status:', response.status);
      console.error('Status Text:', response.statusText);
      console.error('Error Response:', JSON.stringify(errorData, null, 2));

      // Check for specific error reasons
      if (errorData.error) {
        console.error('Error Code:', errorData.error.code);
        console.error('Error Message:', errorData.error.message);
        console.error('Error Status:', errorData.error.status);
        if (errorData.error.details) {
          console.error('Error Details:', JSON.stringify(errorData.error.details, null, 2));
        }
      }
      console.error('================================');
      return [];
    }

    const data = await response.json();
    console.log('Photos API success, albums count:', data.albums?.length || 0);
    return data.albums || [];
  } catch (err) {
    console.error('Photos API fetch error:', err);
    return [];
  }
}

export async function fetchGooglePhotosFromAlbum(
  householdId: string,
  albumId: string,
  pageSize: number = 100
): Promise<any[]> {
  const accessToken = await getValidAccessToken(householdId);
  if (!accessToken) return [];

  try {
    const response = await fetch(
      'https://photoslibrary.googleapis.com/v1/mediaItems:search',
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          albumId,
          pageSize,
        }),
      }
    );

    if (!response.ok) return [];

    const data = await response.json();
    return data.mediaItems || [];
  } catch {
    return [];
  }
}

export async function syncGooglePhotosAlbum(
  householdId: string,
  albumDbId: string,
  googleAlbumId: string
): Promise<number> {
  const photos = await fetchGooglePhotosFromAlbum(householdId, googleAlbumId);
  
  if (photos.length === 0) return 0;

  // Filter to images only (not videos)
  const imagePhotos = photos.filter(p => p.mimeType?.startsWith('image/'));

  const photoRecords = imagePhotos.map(p => ({
    album_id: albumDbId,
    url: `${p.baseUrl}=w1920-h1080`, // Request specific size
    thumbnail_url: `${p.baseUrl}=w400-h300`,
    google_photo_id: p.id,
    taken_at: p.mediaMetadata?.creationTime,
    width: parseInt(p.mediaMetadata?.width) || null,
    height: parseInt(p.mediaMetadata?.height) || null,
  }));

  // Clear existing photos and insert new ones
  await supabase.from('photos').delete().eq('album_id', albumDbId);
  
  const { error } = await supabase.from('photos').insert(photoRecords);

  if (error) {
    console.error('Failed to sync photos:', error);
    return 0;
  }

  // Update last synced timestamp
  await supabase
    .from('photo_albums')
    .update({ last_synced_at: new Date().toISOString() })
    .eq('id', albumDbId);

  return photoRecords.length;
}

// ============================================
// DISCONNECT
// ============================================

export async function disconnectGoogle(householdId: string): Promise<boolean> {
  const { error } = await supabase
    .from('google_tokens')
    .delete()
    .eq('household_id', householdId);

  return !error;
}

export async function isGoogleConnected(householdId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('google_tokens')
      .select('id')
      .eq('household_id', householdId)
      .maybeSingle(); // Use maybeSingle() to return null instead of error when no rows found

    // Return false if error or no data (not connected)
    if (error || !data) {
      return false;
    }

    return true;
  } catch (err) {
    // Handle any unexpected errors gracefully
    console.warn('Error checking Google connection:', err);
    return false;
  }
}
